"use client";

import { c as _c } from "react/compiler-runtime";
import { CheckIcon, CopyIcon } from "lucide-react";
import { createContext, memo, useCallback, use, useEffect, useMemo, useRef, useState } from "react";
import { createHighlighter } from "shiki";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { ComponentProps, CSSProperties, HTMLAttributes } from "react";
import type { BundledLanguage, BundledTheme, HighlighterGeneric, ThemedToken } from "shiki";

// Shiki uses bitflags for font styles: 1=italic, 2=bold, 4=underline
// biome-ignore lint/suspicious/noBitwiseOperators: shiki bitflag check

const isItalic = (fontStyle: number | undefined) => fontStyle && fontStyle & 1;
// biome-ignore lint/suspicious/noBitwiseOperators: shiki bitflag check

// oxlint-disable-next-line eslint(no-bitwise)
const isBold = (fontStyle: number | undefined) => fontStyle && fontStyle & 2;
const isUnderline = (fontStyle: number | undefined) =>
// biome-ignore lint/suspicious/noBitwiseOperators: shiki bitflag check
// oxlint-disable-next-line eslint(no-bitwise)
fontStyle && fontStyle & 4;

// Transform tokens to include pre-computed keys to avoid noArrayIndexKey lint
interface KeyedToken {
  token: ThemedToken;
  key: string;
}
interface KeyedLine {
  tokens: KeyedToken[];
  key: string;
}
const addKeysToTokens = (lines: ThemedToken[][]): KeyedLine[] => lines.map((line, lineIdx) => ({
  key: `line-${lineIdx}`,
  tokens: line.map((token, tokenIdx) => ({
    key: `line-${lineIdx}-${tokenIdx}`,
    token
  }))
}));

// Token rendering component
const TokenSpan = t0 => {
  const $ = _c(10);
  const {
    token
  } = t0;
  const t1 = isItalic(token.fontStyle) ? "italic" : undefined;
  const t2 = isBold(token.fontStyle) ? "bold" : undefined;
  const t3 = isUnderline(token.fontStyle) ? "underline" : undefined;
  let t4;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== t3 || $[3] !== token.bgColor || $[4] !== token.color || $[5] !== token.htmlStyle) {
    t4 = {
      backgroundColor: token.bgColor,
      color: token.color,
      fontStyle: t1,
      fontWeight: t2,
      textDecoration: t3,
      ...token.htmlStyle
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = token.bgColor;
    $[4] = token.color;
    $[5] = token.htmlStyle;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = t4 as CSSProperties;
  let t6;
  if ($[7] !== t5 || $[8] !== token.content) {
    t6 = <span className="dark:!bg-[var(--shiki-dark-bg)] dark:!text-[var(--shiki-dark)]" style={t5}>{token.content}</span>;
    $[7] = t5;
    $[8] = token.content;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
};

// Line rendering component
const LineSpan = t0 => {
  const $ = _c(5);
  const {
    keyedLine,
    showLineNumbers
  } = t0;
  const t1 = showLineNumbers ? LINE_NUMBER_CLASSES : "block";
  let t2;
  if ($[0] !== keyedLine.tokens) {
    t2 = keyedLine.tokens.length === 0 ? "\n" : keyedLine.tokens.map(_temp);
    $[0] = keyedLine.tokens;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = <span className={t1}>{t2}</span>;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};

// Types
type CodeBlockProps = HTMLAttributes<HTMLDivElement> & {
  code: string;
  language: BundledLanguage;
  showLineNumbers?: boolean;
};
interface TokenizedCode {
  tokens: ThemedToken[][];
  fg: string;
  bg: string;
}
interface CodeBlockContextType {
  code: string;
}

// Context
const CodeBlockContext = createContext<CodeBlockContextType>({
  code: ""
});

// Highlighter cache (singleton per language)
const highlighterCache = new Map<string, Promise<HighlighterGeneric<BundledLanguage, BundledTheme>>>();

// Token cache
const tokensCache = new Map<string, TokenizedCode>();

// Subscribers for async token updates
const subscribers = new Map<string, Set<(result: TokenizedCode) => void>>();
const getTokensCacheKey = (code: string, language: BundledLanguage) => {
  const start = code.slice(0, 100);
  const end = code.length > 100 ? code.slice(-100) : "";
  return `${language}:${code.length}:${start}:${end}`;
};
const getHighlighter = (language: BundledLanguage): Promise<HighlighterGeneric<BundledLanguage, BundledTheme>> => {
  const cached = highlighterCache.get(language);
  if (cached) {
    return cached;
  }
  const highlighterPromise = createHighlighter({
    langs: [language],
    themes: ["github-light", "github-dark"]
  });
  highlighterCache.set(language, highlighterPromise);
  return highlighterPromise;
};

// Create raw tokens for immediate display while highlighting loads
const createRawTokens = (code: string): TokenizedCode => ({
  bg: "transparent",
  fg: "inherit",
  tokens: code.split("\n").map(line => line === "" ? [] : [{
    color: "inherit",
    content: line
  } as ThemedToken])
});

// Synchronous highlight with callback for async results
export const highlightCode = (code: string, language: BundledLanguage, callback?: (result: TokenizedCode) => void): TokenizedCode | null => {
  const tokensCacheKey = getTokensCacheKey(code, language);

  // Return cached result if available
  const cached = tokensCache.get(tokensCacheKey);
  if (cached) {
    return cached;
  }

  // Subscribe callback if provided
  if (callback) {
    if (!subscribers.has(tokensCacheKey)) {
      subscribers.set(tokensCacheKey, new Set());
    }
    subscribers.get(tokensCacheKey)?.add(callback);
  }

  // Start highlighting in background - fire-and-forget async pattern
  getHighlighter(language)
  // oxlint-disable-next-line eslint-plugin-promise(prefer-await-to-then)
  .then(highlighter => {
    const availableLangs = highlighter.getLoadedLanguages();
    const langToUse = availableLangs.includes(language) ? language : "text";
    const result = highlighter.codeToTokens(code, {
      lang: langToUse,
      themes: {
        dark: "github-dark",
        light: "github-light"
      }
    });
    const tokenized: TokenizedCode = {
      bg: result.bg ?? "transparent",
      fg: result.fg ?? "inherit",
      tokens: result.tokens
    };

    // Cache the result
    tokensCache.set(tokensCacheKey, tokenized);

    // Notify all subscribers
    const subs = subscribers.get(tokensCacheKey);
    if (subs) {
      for (const sub of subs as any) {
        sub(tokenized);
      }
      subscribers.delete(tokensCacheKey);
    }
  })
  // oxlint-disable-next-line eslint-plugin-promise(prefer-await-to-then), eslint-plugin-promise(prefer-await-to-callbacks)
  .catch(error => {
    console.error("Failed to highlight code:", error);
    subscribers.delete(tokensCacheKey);
  });
  return null;
};

// Line number styles using CSS counters
const LINE_NUMBER_CLASSES = cn("block", "before:content-[counter(line)]", "before:inline-block", "before:[counter-increment:line]", "before:w-8", "before:mr-4", "before:text-right", "before:text-muted-foreground/50", "before:font-mono", "before:select-none");
const CodeBlockBody = memo(t0 => {
  const $ = _c(21);
  const {
    tokenized,
    showLineNumbers,
    className
  } = t0;
  let t1;
  if ($[0] !== tokenized.bg || $[1] !== tokenized.fg) {
    t1 = {
      backgroundColor: tokenized.bg,
      color: tokenized.fg
    };
    $[0] = tokenized.bg;
    $[1] = tokenized.fg;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const preStyle = t1;
  let t2;
  if ($[3] !== tokenized.tokens) {
    t2 = addKeysToTokens(tokenized.tokens);
    $[3] = tokenized.tokens;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const keyedLines = t2;
  let t3;
  if ($[5] !== className) {
    t3 = cn("dark:bg-(--shiki-dark-bg)! dark:text-(--shiki-dark)! m-0 p-4 text-sm", className);
    $[5] = className;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = showLineNumbers && "[counter-increment:line_0] [counter-reset:line]";
  let t5;
  if ($[7] !== t4) {
    t5 = cn("font-mono text-sm", t4);
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== keyedLines || $[10] !== showLineNumbers) {
    let t7;
    if ($[12] !== showLineNumbers) {
      t7 = keyedLine => <LineSpan key={keyedLine.key} keyedLine={keyedLine} showLineNumbers={showLineNumbers} />;
      $[12] = showLineNumbers;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    t6 = keyedLines.map(t7);
    $[9] = keyedLines;
    $[10] = showLineNumbers;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = <code className={t5}>{t6}</code>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== preStyle || $[18] !== t3 || $[19] !== t7) {
    t8 = <pre className={t3} style={preStyle}>{t7}</pre>;
    $[17] = preStyle;
    $[18] = t3;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}, (prevProps, nextProps) => prevProps.tokenized === nextProps.tokenized && prevProps.showLineNumbers === nextProps.showLineNumbers && prevProps.className === nextProps.className);
CodeBlockBody.displayName = "CodeBlockBody";
export const CodeBlockContainer = t0 => {
  const $ = _c(14);
  let className;
  let language;
  let props;
  let style;
  if ($[0] !== t0) {
    ({
      className,
      language,
      style,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = language;
    $[3] = props;
    $[4] = style;
  } else {
    className = $[1];
    language = $[2];
    props = $[3];
    style = $[4];
  }
  let t1;
  if ($[5] !== className) {
    t1 = cn("group relative w-full overflow-hidden rounded-md border bg-background text-foreground", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== style) {
    t2 = {
      containIntrinsicSize: "auto 200px",
      contentVisibility: "auto",
      ...style
    };
    $[7] = style;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== language || $[10] !== props || $[11] !== t1 || $[12] !== t2) {
    t3 = <div className={t1} data-language={language} style={t2} {...props} />;
    $[9] = language;
    $[10] = props;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
};
export const CodeBlockHeader = t0 => {
  const $ = _c(10);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("flex items-center justify-between border-b bg-muted/80 px-3 py-2 text-muted-foreground text-xs", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== t1) {
    t2 = <div className={t1} {...props}>{children}</div>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
export const CodeBlockTitle = t0 => {
  const $ = _c(10);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("flex items-center gap-2", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== t1) {
    t2 = <div className={t1} {...props}>{children}</div>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
export const CodeBlockFilename = t0 => {
  const $ = _c(10);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("font-mono", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== t1) {
    t2 = <span className={t1} {...props}>{children}</span>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
export const CodeBlockActions = t0 => {
  const $ = _c(10);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("-my-1 -mr-1 flex items-center gap-2", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== t1) {
    t2 = <div className={t1} {...props}>{children}</div>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
export const CodeBlockContent = t0 => {
  const $ = _c(14);
  const {
    code,
    language,
    showLineNumbers: t1
  } = t0;
  const showLineNumbers = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== code) {
    t2 = createRawTokens(code);
    $[0] = code;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const rawTokens = t2;
  let t3;
  if ($[2] !== code || $[3] !== language || $[4] !== rawTokens) {
    t3 = () => highlightCode(code, language) ?? rawTokens;
    $[2] = code;
    $[3] = language;
    $[4] = rawTokens;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const [tokenized, setTokenized] = useState(t3);
  let t4;
  let t5;
  if ($[6] !== code || $[7] !== language || $[8] !== rawTokens) {
    t4 = () => {
      let cancelled = false;
      setTokenized(highlightCode(code, language) ?? rawTokens);
      highlightCode(code, language, result => {
        if (!cancelled) {
          setTokenized(result);
        }
      });
      return () => {
        cancelled = true;
      };
    };
    t5 = [code, language, rawTokens];
    $[6] = code;
    $[7] = language;
    $[8] = rawTokens;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useEffect(t4, t5);
  let t6;
  if ($[11] !== showLineNumbers || $[12] !== tokenized) {
    t6 = <div className="relative overflow-auto"><CodeBlockBody showLineNumbers={showLineNumbers} tokenized={tokenized} /></div>;
    $[11] = showLineNumbers;
    $[12] = tokenized;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
};
export const CodeBlock = t0 => {
  const $ = _c(22);
  let children;
  let className;
  let code;
  let language;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      code,
      language,
      showLineNumbers: t1,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = code;
    $[4] = language;
    $[5] = props;
    $[6] = t1;
  } else {
    children = $[1];
    className = $[2];
    code = $[3];
    language = $[4];
    props = $[5];
    t1 = $[6];
  }
  const showLineNumbers = t1 === undefined ? false : t1;
  let t2;
  if ($[7] !== code) {
    t2 = {
      code
    };
    $[7] = code;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const contextValue = t2;
  let t3;
  if ($[9] !== code || $[10] !== language || $[11] !== showLineNumbers) {
    t3 = <CodeBlockContent code={code} language={language} showLineNumbers={showLineNumbers} />;
    $[9] = code;
    $[10] = language;
    $[11] = showLineNumbers;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== children || $[14] !== className || $[15] !== language || $[16] !== props || $[17] !== t3) {
    t4 = <CodeBlockContainer className={className} language={language} {...props}>{children}{t3}</CodeBlockContainer>;
    $[13] = children;
    $[14] = className;
    $[15] = language;
    $[16] = props;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== contextValue || $[20] !== t4) {
    t5 = <CodeBlockContext value={contextValue}>{t4}</CodeBlockContext>;
    $[19] = contextValue;
    $[20] = t4;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  return t5;
};
export type CodeBlockCopyButtonProps = ComponentProps<typeof Button> & {
  onCopy?: () => void;
  onError?: (error: Error) => void;
  timeout?: number;
};
export const CodeBlockCopyButton = ({
  onCopy,
  onError,
  timeout = 2000,
  children,
  className,
  ...props
}: CodeBlockCopyButtonProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef<number>(0);
  const {
    code
  } = use(CodeBlockContext);
  const copyToClipboard = useCallback(async () => {
    if (typeof window === "undefined" || !navigator?.clipboard?.writeText) {
      onError?.(new Error("Clipboard API not available"));
      return;
    }
    try {
      if (!isCopied) {
        await navigator.clipboard.writeText(code);
        setIsCopied(true);
        onCopy?.();
        timeoutRef.current = window.setTimeout(() => setIsCopied(false), timeout);
      }
    } catch (error) {
      onError?.(error as Error);
    }
  }, [code, onCopy, onError, timeout, isCopied]);
  useEffect(() => () => {
    window.clearTimeout(timeoutRef.current);
  }, []);
  const Icon = isCopied ? CheckIcon : CopyIcon;
  return <Button className={cn("shrink-0", className)} onClick={copyToClipboard} size="icon" variant="ghost" {...props}>
			{children ?? <Icon size={14} />}
		</Button>;
};
export type CodeBlockLanguageSelectorProps = ComponentProps<typeof Select>;
export const CodeBlockLanguageSelector = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Select {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export type CodeBlockLanguageSelectorTriggerProps = ComponentProps<typeof SelectTrigger>;
export const CodeBlockLanguageSelectorTrigger = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("h-7 border-none bg-transparent px-2 text-xs shadow-none", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectTrigger className={t1} size="sm" {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type CodeBlockLanguageSelectorValueProps = ComponentProps<typeof SelectValue>;
export const CodeBlockLanguageSelectorValue = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <SelectValue {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export type CodeBlockLanguageSelectorContentProps = ComponentProps<typeof SelectContent>;
export const CodeBlockLanguageSelectorContent = t0 => {
  const $ = _c(6);
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      align: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
  } else {
    props = $[1];
    t1 = $[2];
  }
  const align = t1 === undefined ? "end" : t1;
  let t2;
  if ($[3] !== align || $[4] !== props) {
    t2 = <SelectContent align={align} {...props} />;
    $[3] = align;
    $[4] = props;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
export type CodeBlockLanguageSelectorItemProps = ComponentProps<typeof SelectItem>;
export const CodeBlockLanguageSelectorItem = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <SelectItem {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
function _temp(t0) {
  const {
    token,
    key
  } = t0;
  return <TokenSpan key={key} token={token} />;
}